import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { FaHandHoldingUsd, FaSave } from 'react-icons/fa';

import { getTipSettings, updateTipSettings } from '../../services/api';
import { formatPriceWithSymbol } from '../../utils/currency';

/**
 * Aba "Gorjeta" das Informações da Loja.
 *
 * Duas escolhas de produto que a tela precisa deixar explícitas:
 *
 * 1. O modo padrão é SUGERIDA. Em muitos países (Brasil incluso) os 10% são
 *    legalmente opcionais, então "fixa" traz risco jurídico — a tela avisa em vez
 *    de deixar o dono descobrir depois.
 * 2. A gorjeta NÃO é faturamento da loja: é da equipe. A tela diz isso, porque o
 *    dono vai estranhar quando o relatório de vendas não a contar.
 */
function GorjetaConfig() {
    const { t } = useTranslation('storeInfo');

    const [carregando, setCarregando] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const [canais, setCanais] = useState(['mesa']);
    const [form, setForm] = useState({
        habilitado: false,
        percentual: 10,
        modo: 'sugerida',
        aplica_em: ['mesa'],
    });

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const { data } = await getTipSettings();
            const g = data?.gorjeta || {};
            setForm({
                habilitado: g.habilitado === true,
                percentual: g.percentual ?? 10,
                modo: g.modo || 'sugerida',
                aplica_em: Array.isArray(g.aplica_em) && g.aplica_em.length ? g.aplica_em : ['mesa'],
            });
            setCanais(data?.canais_disponiveis || ['mesa']);
        } catch (error) {
            console.error('[GORJETA] Erro ao carregar:', error);
            toast.error(t('tip.loadError', 'Nao foi possivel carregar a configuracao de gorjeta.'));
        } finally {
            setCarregando(false);
        }
    }, [t]);

    useEffect(() => { carregar(); }, [carregar]);

    const alterar = (campo, valor) => setForm((prev) => ({ ...prev, [campo]: valor }));

    const alternarCanal = (canal) => {
        setForm((prev) => {
            const tem = prev.aplica_em.includes(canal);
            const novo = tem ? prev.aplica_em.filter((c) => c !== canal) : [...prev.aplica_em, canal];
            // Nunca deixar vazio: sem canal nenhum, "habilitado" não significaria nada.
            return { ...prev, aplica_em: novo.length ? novo : prev.aplica_em };
        });
    };

    const salvar = async () => {
        if (form.habilitado) {
            const pct = Number(form.percentual);
            if (!Number.isFinite(pct) || pct <= 0 || pct > 30) {
                return toast.warning(t('tip.invalidPercent', 'Informe um percentual entre 0 e 30.'));
            }
        }

        setSalvando(true);
        try {
            await updateTipSettings(
                form.habilitado
                    ? {
                        habilitado: true,
                        percentual: Number(form.percentual),
                        modo: form.modo,
                        aplica_em: form.aplica_em,
                    }
                    : { habilitado: false }
            );
            toast.success(t('tip.saved', 'Configuracao de gorjeta salva.'));
            carregar();
        } catch (error) {
            toast.error(error?.response?.data?.msg || t('tip.saveError', 'Nao foi possivel salvar.'));
            console.error('[GORJETA] Erro ao salvar:', error);
        } finally {
            setSalvando(false);
        }
    };

    if (carregando) {
        return (
            <div className="modern-loading">
                <div className="modern-spinner"></div>
                <p>{t('loading', 'Carregando...')}</p>
            </div>
        );
    }

    // Exemplo concreto sobre uma conta de 100 na moeda da loja: o dono confere de cabeça.
    const pctNum = Number(form.percentual);
    const exemplo = Number.isFinite(pctNum) && pctNum > 0 ? pctNum : 0;

    return (
        <div className="info-loja-dados-content">
            <div className="info-loja-dados-section-header">
                <div className="info-loja-dados-header">
                    <div className="info-loja-dados-header-icon"><FaHandHoldingUsd /></div>
                    <div>
                        <h2>{t('tip.title', 'Gorjeta')}</h2>
                        <p>{t('tip.subtitle', 'Taxa de servico sugerida no fechamento da conta.')}</p>
                    </div>
                </div>
                <button className="modern-btn modern-btn-save" onClick={salvar} disabled={salvando}>
                    <FaSave /> {salvando ? t('saving', 'Salvando...') : t('save', 'Salvar')}
                </button>
            </div>

            <div className="moeda-config-form">
                <label className="moeda-config-toggle">
                    <input
                        type="checkbox"
                        checked={form.habilitado}
                        onChange={(e) => alterar('habilitado', e.target.checked)}
                    />
                    <span>{t('tip.enable', 'Cobrar gorjeta')}</span>
                </label>

                {!form.habilitado && (
                    <p className="moeda-config-hint">
                        {t('tip.disabledHint', 'Desligado, nenhuma gorjeta e sugerida no fechamento.')}
                    </p>
                )}

                {form.habilitado && (
                    <>
                        <div className="moeda-config-field">
                            <label>{t('tip.percent', 'Percentual')}</label>
                            <input
                                type="number"
                                min="0"
                                max="30"
                                step="0.5"
                                value={form.percentual}
                                onChange={(e) => alterar('percentual', e.target.value)}
                            />
                            {exemplo > 0 && (
                                <small>
                                    {t('tip.percentExample', 'Em uma conta de {{conta}}, a gorjeta sera de {{valor}}.', {
                                        conta: formatPriceWithSymbol(100),
                                        valor: formatPriceWithSymbol(exemplo),
                                    })}
                                </small>
                            )}
                        </div>

                        <div className="moeda-config-field">
                            <label>{t('tip.mode', 'Como cobrar')}</label>
                            <select value={form.modo} onChange={(e) => alterar('modo', e.target.value)}>
                                <option value="sugerida">{t('tip.modeSuggested', 'Sugerida — o cliente pode recusar')}</option>
                                <option value="fixa">{t('tip.modeFixed', 'Fixa — sempre aplicada')}</option>
                            </select>
                            {form.modo === 'fixa' && (
                                <small className="gorjeta-config-alerta">
                                    {t('tip.fixedWarning', 'Atencao: em muitos paises a gorjeta e legalmente opcional e nao pode ser imposta. Confirme a regra local antes de usar este modo.')}
                                </small>
                            )}
                        </div>

                        <div className="moeda-config-field">
                            <label>{t('tip.channels', 'Onde aplicar')}</label>
                            <div className="gorjeta-config-canais">
                                {canais.map((c) => (
                                    <label key={c} className="gorjeta-config-canal">
                                        <input
                                            type="checkbox"
                                            checked={form.aplica_em.includes(c)}
                                            onChange={() => alternarCanal(c)}
                                        />
                                        <span>{t('tip.channel.' + c, c)}</span>
                                    </label>
                                ))}
                            </div>
                            <small>{t('tip.channelsHint', 'Em delivery a gorjeta costuma ser do entregador, nao do garcom.')}</small>
                        </div>

                        {/* O dono precisa saber disso ANTES de estranhar o relatorio. */}
                        <p className="moeda-config-hint">
                            {t('tip.notRevenueHint', 'A gorjeta e registrada separada do faturamento: ela entra no caixa, mas nao conta como venda da loja nos relatorios.')}
                        </p>
                    </>
                )}
            </div>
        </div>
    );
}

export default GorjetaConfig;
